// Deterministic RNG + uniform grid. No rendering imports.

export class Rng {
  private s: number;
  constructor(seed: number) { this.s = seed >>> 0 || 1; }
  next(): number {
    let t = (this.s = (this.s + 0x6d2b79f5) >>> 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number): number { return a + (b - a) * this.next(); }
  int(n: number): number { return Math.floor(this.next() * n); }
  pick<T>(arr: readonly T[]): T { return arr[this.int(arr.length)]; }
}

/** Uniform grid broadphase over the XZ plane (linked lists in typed arrays). */
export class Grid {
  readonly cell: number;
  readonly half: number;
  readonly dim: number;
  readonly head: Int32Array;
  next: Int32Array;
  constructor(half: number, cell: number, capacity: number) {
    this.half = half; this.cell = cell;
    this.dim = Math.ceil((half * 2) / cell);
    this.head = new Int32Array(this.dim * this.dim).fill(-1); // empty until the first build (querying earlier must not cycle)
    this.next = new Int32Array(capacity);
  }
  clear(): void { this.head.fill(-1); }
  cellOf(x: number, z: number): number {
    let cx = Math.floor((x + this.half) / this.cell);
    let cz = Math.floor((z + this.half) / this.cell);
    cx = cx < 0 ? 0 : cx >= this.dim ? this.dim - 1 : cx;
    cz = cz < 0 ? 0 : cz >= this.dim ? this.dim - 1 : cz;
    return cz * this.dim + cx;
  }
  insert(i: number, x: number, z: number): void {
    const c = this.cellOf(x, z);
    this.next[i] = this.head[c];
    this.head[c] = i;
  }
  /** Calls fn for every index in cells overlapping the circle. */
  query(x: number, z: number, r: number, fn: (i: number) => void): void {
    const d = this.dim;
    const x0 = Math.max(0, Math.floor((x - r + this.half) / this.cell));
    const x1 = Math.min(d - 1, Math.floor((x + r + this.half) / this.cell));
    const z0 = Math.max(0, Math.floor((z - r + this.half) / this.cell));
    const z1 = Math.min(d - 1, Math.floor((z + r + this.half) / this.cell));
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        for (let i = this.head[cz * d + cx]; i !== -1; i = this.next[i]) fn(i);
      }
    }
  }
}
